import { IconAspectRatio, IconCrop169, IconCropLandscape, IconPencil, IconPhoto, IconSquare, IconTrash } from '@tabler/icons-react';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { uploadImage } from '../../../../api/api.global';
import { getApi, postApi } from '../../../../api/call.api';
import { urlApi } from '../../../../api/url.api';
import IconButton from '../../../../components/Buttons/IconButton';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';
import ToggleBtn from '../../../../components/Toggle/ToggleBtn';
import { loadingHide, loadingShow } from '../../../../loadingUi/loading.controller';
import { disable, enable } from '../../../../redux/features/blockEdit/blockEditSlice';
import EditLinkOfBlock from '../EditLinkOfBlock';
import { ensureHttpPrefix } from '../../../../utils/url.utils';

const ImageBlockDragAndEdit = React.memo(({ data, callback, dragButton }) => {

    const { pageId } = useParams();
    const imageUploadInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [aspectRatio, setAspectRatio] = useState(data?.images[0]?.aspectRatio || null);
    const dispatch = useDispatch();
    const isEditable = useSelector((state) => state.isBlockEdit?.value);

    const editableName = ('image-block-' + data?._id)

    const handleOnEditClick = () => {
        dispatch(enable(editableName))
    }

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        const maxSizeInBytes = 1024 * 1024 * 5;
        if (file && file.size > maxSizeInBytes) {
            toast.error('File size exceeds the limit of 5MB.');
            setFile(null);
            e.target.value = null;
        } else {
            setFile(file);
        }
    }

    async function updateStatus(status) {
        let url = `${urlApi.updateBlockStatus}${pageId}/${data?._id}/${status}`;
        loadingShow();
        let resp = await getApi(url);
        loadingHide();
        if (resp.responseCode === 200) {
            if (callback) {
                callback();
            }
            toast.success(resp.message);
        } else {
            toast.error(resp.message);
        }
    }

    async function update(imageData) {
        loadingShow();
        let url = `${urlApi.updateBlockDetails}${pageId}/${data?._id}/${imageData?._id}`;
        let postData = {
            title: document.getElementById('title-' + imageData?._id).value?.trim(),
            description: document.getElementById('description-' + imageData?._id).value?.trim(),
            destinationUrl: ensureHttpPrefix(document.getElementById('destinationUrl-' + imageData?._id).value?.trim()),
            aspectRatio: aspectRatio
        }
        if (file || imageData?.image) {
            postData.image = file ? await uploadImage(file, 'advy-pages') : imageData?.image
        }

        let resp = await postApi(postData, url);
        loadingHide();
        if (resp.responseCode === 200) {
            if (callback) {
                callback();
            }
            dispatch(disable(''))
            toast.success(resp.message);
        } else {
            toast.error(resp.message);
        }
    }

    return (
        <div className='w-full'>
            <div className='flex gap-3 items-center w-full mb-2'>
                {isEditable != editableName && dragButton}
                <div className="flex gap-1 items-center">
                    <IconPhoto size={16} />
                    <p className="mb-0 font-medium text-xs">Image</p>
                </div>
                {(isEditable != editableName) && <IconButton cssClass={'ms-auto'} icon={<IconPencil size={18} />} text={"Edit"} onClickFunction={() => handleOnEditClick()} />}
                {isEditable != editableName && <ConfirmationModal buttonComponent={<IconButton icon={<IconTrash size={18} />} />} confirmationText={"Are you sure want ro remove this block?"} onConfirm={() => updateStatus(3)} />}
            </div>

            <div className={`${isEditable == editableName ? 'ps-0' : 'ps-4'}`}>
                {isEditable == editableName ?
                    <div className='w-full'>
                        {
                            data?.images?.map((item, i) => {
                                return (
                                    <div key={i}>
                                        <div className="flex gap-3">
                                            <div className='w-40'>
                                                <input type="file" accept="image/*" name="" id="" className="hidden" onChange={handleImageChange} ref={imageUploadInputRef} />
                                                <button onClick={() => imageUploadInputRef.current.click()}>
                                                    <img src={file ? URL.createObjectURL(file) : item?.image ? item?.image : (process.env.PUBLIC_URL + "/images/image-upload.png")} id={'image-preview-' + data?._id} className='cursor-pointer  rounded-lg object-cover' alt="" style={{ aspectRatio: aspectRatio }} />
                                                </button>
                                                <p className="text-xs text-gray-400">*Maximum size: 5MB</p>
                                            </div>
                                            <div className='w-full'>
                                                <p className="text-md font-semibold mb-1.5">Title & Description</p>
                                                <input type="text" className="form-control mb-2 w-full" placeholder="Enter Title" id={'title-' + item?._id} defaultValue={item?.title} maxLength={255} />
                                                <input type="text" className="form-control w-full" placeholder="Enter Description (Optional) (Max 140 Words)" id={'description-' + item?._id} defaultValue={item?.description} maxLength={140} />
                                            </div>
                                        </div>

                                        <div className='mb-3'>
                                            <p className='mb-1 font-semibold'>Aspect Ratio</p>
                                            <div className="flex flex-wrap gap-3">
                                                <button className={`flex items-center gap-2 text-sm font-semibold border-2 ${aspectRatio == "auto" && 'border-primary'} px-3 py-1.5 rounded-lg`} onClick={() => setAspectRatio('auto')} > <IconAspectRatio size={22} /> Original </button>
                                                <button className={`flex items-center gap-2 text-sm font-semibold border-2 ${aspectRatio == "1/1" && 'border-primary'} px-3 py-1.5 rounded-lg`} onClick={() => setAspectRatio('1/1')}> <IconSquare size={22} /> 1:1 </button>
                                                <button className={`flex items-center gap-2 text-sm font-semibold border-2 ${aspectRatio == "3/2" && 'border-primary'} px-3 py-1.5 rounded-lg`} onClick={() => setAspectRatio('3/2')}> <IconCropLandscape size={22} /> 3:2 </button>
                                                <button className={`flex items-center gap-2 text-sm font-semibold border-2 ${aspectRatio == "16/9" && 'border-primary'} px-3 py-1.5 rounded-lg`} onClick={() => setAspectRatio('16/9')}> <IconCrop169 size={22} /> 16:9 </button>
                                            </div>
                                        </div>

                                        <EditLinkOfBlock blockData={item} />

                                        <div className="flex justify-end items-center mt-3 gap-3">
                                            <button className="font-semibold text-sm border-2 border-primary px-3 py-1.5 rounded-full" onClick={() => dispatch(disable(''))}>Cancel</button>
                                            <button className="font-semibold text-sm text-white bg-primary px-3 py-2 rounded-full" onClick={() => update(item)}>Update</button>
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>
                    :
                    <div className='w-full'>
                        {
                            data?.images?.map((item, i) => {
                                return (
                                    <div className="flex gap-3 mb-2" key={i}>
                                        <img src={item?.image ? (item?.image) : process.env.PUBLIC_URL + "/images/image-upload.png"} alt="" className='h-20 rounded-md' />
                                        <div>
                                            <p className='mb-1 font-semibold'>{item?.title}</p>
                                            <p className="mb-0 text-xs text-[#686C8F]">{item?.description}</p>
                                            <a href={item?.link} className='text-xs text-primary hover:text-primary-dark'>{item?.link}</a>
                                        </div>
                                    </div>)
                            })
                        }
                        <div className="flex justify-end">
                            <ToggleBtn state={data?.status == 1} onClickFunction={() => {
                                if (data?.status == 1) {
                                    updateStatus(2)
                                } if (data?.status == 2) {
                                    updateStatus(1)
                                }
                            }} />
                        </div>
                    </div>}
            </div>
        </div>
    )
})

export default ImageBlockDragAndEdit